import { nextTick, inject, Ref, ref, watch, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { getSessionStorage, setSessionStorage } from '../utils/storage';
import JSBridge from '../bridge';
import { submitTrack, initThinkingData } from '../utils/tracking';

export interface TrackingOptions {
  account?: string;
  name?: string;
  data?: {
    PageID?: string | number;
    PageName?: string;
    Result?: string;
    VisibilityInfo?: string;
    BeforeInfo?: any;
    ContentType?: number;
    ContentName?: string;
    ActionName?: string;
    ActionID?: number;
    serverID?: number | string;
  } & Record<string, any>;
  Scene?: number;
  StayDuration?: string;
  ServerID?: number | string;
  pageName?: string;
  pageID?: string | number;
}
interface StayTrackingOptions extends TrackingOptions {
  isStay?: boolean;
}

export function useTracking(option?: { stayTrackingOptions?: StayTrackingOptions }) {
  const router = useRouter();
  let stayData: TrackingOptions = option?.stayTrackingOptions || {}; // 存储停留追踪的配置数据

  // 格式化追踪参数
  const formatTrackingParams = (options: TrackingOptions, defaultEventName: string, eventMode?: string) => {
    let PageName = options.pageName || options.data?.PageName || router.currentRoute.value.name;
    let PageID = options.pageID || options.data?.PageID;
    let Scene = options.Scene;
    let StayDuration = options.StayDuration;
    let ServerID = options.ServerID;
    return {
      account: options.account || '',
      eventName: options.name || defaultEventName,
      eventMode: eventMode || '',
      trackData: { ...options.data, PageID, PageName, Scene, StayDuration, ServerID },
      pageName: PageName
    };
  };

  // 页面访问埋点
  const trackVisit = (options: TrackingOptions) => {
    const format = formatTrackingParams(options, 'EventPageVisit', 'visit');
    initThinkingData(format);

    // 在 nextTick 中执行，确保组件已挂载
    nextTick(async () => {
      // 获取路由信息
      let pageInfo = {};
      try {
        if (router.currentRoute?.value) {
          pageInfo = router.currentRoute.value.meta?.pageInfo || {};
        }
      } catch (error) {
        console.warn('获取页面信息失败:', error);
      }

      format.trackData = { ...format.trackData, ...pageInfo };

      // 处理前页信息逻辑
      const beforeInfos = getSessionStorage('beforeInfos') ? JSON.parse(getSessionStorage('beforeInfos')!) : [];
      const prevPage = history.state.back || '#native';
      const beforeInfo = beforeInfos?.find((item: any) => item.fullPath == prevPage);

      if (beforeInfo) {
        format.trackData.BeforeInfo = JSON.stringify({
          ...beforeInfo,
          ...(typeof format.trackData.BeforeInfo === 'object' ? format.trackData.BeforeInfo : {})
        });
        submitTrack(format.eventName, format.trackData);
      } else {
        JSBridge.callAsync('getBeforeInfo')
          .then((result: any) => {
            let beforeInfo = { ...result, ...format.trackData.BeforeInfo };
            format.trackData.BeforeInfo = JSON.stringify(beforeInfo);
            submitTrack(format.eventName, format.trackData);
            beforeInfos.unshift({ fullPath: '#native', pageInfo: { ...result, ...beforeInfo } });
            setSessionStorage('beforeInfos', JSON.stringify(beforeInfos)); // 缓存上个页面
          })
          .catch((error: any) => {
            submitTrack(format.eventName, format.trackData);
          });
      }
    });
  };

  // 点击埋点
  const trackClick = (options: TrackingOptions) => {

    const format = formatTrackingParams(options, 'EventPageClick', 'click');
    initThinkingData(format);
    submitTrack(format.eventName, format.trackData);
  };

  // 曝光埋点
  const trackExposure = (options: { account?: string; name: string; data: any }) => {
    const format = formatTrackingParams(options, options.name, 'exposure');
    initThinkingData(format);
    submitTrack(options.name, options.data);
  };

  // 手动结算一次停留：由调用方决定结算时机（路由切换、应用隐藏等）
  // 适用于停留时段不由 isH5Hidden + 组件卸载驱动的场景（如 keep-alive 内切换页面）
  const reportStay = (options: TrackingOptions & { StayDuration: string | number }) => {
    const format = formatTrackingParams({ ...stayData, ...options }, 'PageStay');
    initThinkingData(format);
    submitTrack(format?.eventName, format?.trackData);
  };

  //停留埋点
  const trackStay = () => {
    const isH5Hidden = inject<Ref>('isH5Hidden', ref(false));
    let enterTime = 0;
    let prevHidden: boolean | undefined = undefined;

    function report(reason: 'hidden' | 'unmount') {
      if (!enterTime) return;

      const stay = ((Date.now() - enterTime) / 1000).toFixed(3);

      // 停留时间小于 0.4s 不上报
      if (parseFloat(stay) < 0.4) {
        enterTime = 0;
        return;
      }

      reportStay({ StayDuration: stay });

      enterTime = 0; // 结算后清零，等待下次 show 再开始
    }

    watch(
      isH5Hidden,
      (hidden) => {
        // 第一次进来只做初始化，不上报
        if (prevHidden === undefined) {
          prevHidden = hidden;
          if (hidden === false) enterTime = Date.now();
          return;
        }

        // visible -> hidden：立刻上报（允许重复上报，每次隐藏一次就一条）
        if (prevHidden === false && hidden === true) {
          report('hidden');
        }

        // hidden -> visible：重新开始计时
        if (prevHidden === true && hidden === false) {
          enterTime = Date.now();
        }

        prevHidden = hidden;
      },
      { immediate: true }
    );
    // 可选：路由跳转/组件卸载时，如果仍可见，结算一次（不影响“隐藏就上报”）
    onBeforeUnmount(() => {
      report('unmount');
    });
  };

  //停留埋点
  if (option?.stayTrackingOptions?.isStay) {
    trackStay();
  }

  // 更新停留追踪的配置数据
  const updateStayTracking = (data: TrackingOptions) => {
    stayData = { ...stayData, ...data };
  };

  // 只导出公共 API，内部函数不导出
  return {
    trackVisit,
    trackClick,
    trackExposure,
    reportStay,
    updateStayTracking
  };
}
